import './movement.scss'

import * as _ from 'lodash'
import { combineLatest } from 'rxjs'
import { Component, Watch } from 'vue-property-decorator'
import { Route } from 'vue-router'
import {
    faAngleDown,
    faAngleRight,
    faCircleNotch,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons'

import { LinkableItemLifecycleStates, Item, Movement, MovementLifecycleStates, ThesaurusTerm, Featured, Task, Container, ItemTypeWrapper, getTranslatedString } from '@skinsoft/types'
import { itemSelectors, locationSelectors, movementSelectors, taskSelectors, thesaurusTermSelectors } from '@skinsoft/selectors'
import {
    ContainerDataStore,
    ItemDataStore,
    itemSorters,
    MovementDataStore,
    TaskDataStore,
    ThesaurusTermDataStore,
} from '@skinsoft/entity'
import { ReactiveVue } from '@skinsoft/reactive-vue'
import { ContainerCard, MovementFeaturedCard, MovementItemCard } from '@skinsoft/entity-ui'
import { ItemCard, PullToRight } from '@skinsoft/ui'

import {
    ADD_ACCESSORY_TOPIC,
    ADD_CONTAINER_TOPIC,
    ADD_ITEM_TOPIC,
    CANCEL_MOVEMENT_TOPIC,
    CLOSE_MOVEMENT_TOPIC,
    DELETE_MOVEMENT_TOPIC,
    RETURN_MOVEMENT_TOPIC,
    START_ITEM_TRANSIT_TOPIC,
    TURN_ON_BARCODE_READER_TOPIC,
} from '../../../busTopics'
import { Routes, router } from '../../../router'
import { CloseMovementModal } from '../../component/close-movement-modal'
import { BAR_CODE_READER_CLICK } from '@skinsoft/entity-ui/src/movement/component/movement-item-card/identifier-input'
import { map, tap } from 'rxjs/operators'
import { SelectItemMultipleModal } from '../../component/select-item-multiple-modal'
import {
    getClassNameFromContentTypeKey,
    getContentTypeFromTypeKey,
    ListContentType,
} from '../container/list-content-type'
import { DELETE_CARD, MOVEMENT_ITEM_CARD_LOCATION_CHANGED, UPDATE_ITEM_MOVEMENT_PAGE } from '@skinsoft/events'
import { ACCESSORY_TYPES_FOR_SEARCH, CONTAINER_TYPES_FOR_SEARCH, DataStore } from '@skinsoft/data-store'
import { BModal } from 'bootstrap-vue'
import { STOP_ASKING_FOR_MISSING_LOCATION_TOPIC } from '@skinsoft/bus'

type ItemSorts = 'ITEM' | 'CONTAINER' | 'ACCESSORY'

@Component({
    components: {
        sSelectItemMultipleModal: SelectItemMultipleModal,
        sCloseMovementModal: CloseMovementModal,
        sItemCard: ItemCard,
        sMovementFeaturedCard: MovementFeaturedCard,
        sMovementItemCard: MovementItemCard,
        sPullToRight: PullToRight,
        sContainerCard: ContainerCard,
    },
    template: require('./movement.html'),
})
export class MovementPage extends ReactiveVue {
    public $movementDataStore: MovementDataStore
    public angleDownIcon = faAngleDown
    public angleRightIcon = faAngleRight
    public spinnerIcon = faCircleNotch
    public movement: Movement = null as any
    public motive: ThesaurusTerm = null as any
    public associatedTask: Task = null as any
    public $route: Route
    public $itemDataStore: ItemDataStore
    public $taskDataStore: TaskDataStore
    public $thesaurusTermDataStore: ThesaurusTermDataStore
    public $containerDataStore: ContainerDataStore
    public $refs: {
        fakeEntity: HTMLDivElement
        item: ItemCard[]
        addItemModal: SelectItemMultipleModal
        closeMovementModal: CloseMovementModal
        puller: PullToRight
        viewport: HTMLDivElement
        addContainerModal: SelectItemMultipleModal
        addAccessoryModal: SelectItemMultipleModal,
        returnMovementModal: BModal
    }
    public currentlyAddingTo: ListContentType | null = null
    ListContentType = ListContentType

    public itemTypes = ['physicalMuseumItem', 'numericMuseumItem']
    public accessoryTypes = ACCESSORY_TYPES_FOR_SEARCH
    public containerTypes = CONTAINER_TYPES_FOR_SEARCH

    public movementCreationPending = false
    public showCollapse = false
    public addItemModalMounted: boolean = false
    public addAccessoryModalMounted: boolean = false
    public addContainerModalMounted: boolean = false
    public exclamationTriangleIcon = faExclamationTriangle
    public closeMovementModalMounted: boolean = false
    public featured: Featured = null as any
    public focusedItemId: Item['id'] = null as any
    public items: Item[] = [null, null, null] as any[]
    public containers: ItemTypeWrapper[] = [] // ContainerCard needs ItemTypeWrapper
    public accessories: Item[] = []

    public committedItems: string[] = []

    public DELETE_CARD = DELETE_CARD
    private MOVEMENT_ITEM_CARD_LOCATION_CHANGED = MOVEMENT_ITEM_CARD_LOCATION_CHANGED
    private UPDATE_ITEM_MOVEMENT_PAGE = UPDATE_ITEM_MOVEMENT_PAGE

    get title() {
        return movementSelectors.title(this.movement)
    }

    get associatedTaskIncrementalTitle() {
        return taskSelectors.incrementalTitle(this.associatedTask)
    }

    public mounted() {
        this.subscribeToBus(ADD_ITEM_TOPIC, this.addItem.bind(this))
        this.subscribeToBus(CANCEL_MOVEMENT_TOPIC, this.cancelMovement.bind(this))
        this.subscribeToBus(CLOSE_MOVEMENT_TOPIC, this.closeMovement.bind(this))
        this.subscribeToBus(DELETE_MOVEMENT_TOPIC, this.deleteMovement.bind(this))
        this.subscribeToBus(RETURN_MOVEMENT_TOPIC, this.askConfirmationForReturnMovement.bind(this))
        this.subscribeToBus(BAR_CODE_READER_CLICK, () =>
            this.emitOnBus(TURN_ON_BARCODE_READER_TOPIC),
        )
        this.subscribeToBus(START_ITEM_TRANSIT_TOPIC, this.startItemTransit.bind(this))
        this.subscribeToBus(ADD_CONTAINER_TOPIC, this.addContainer.bind(this))
        this.subscribeToBus(ADD_ACCESSORY_TOPIC, this.addAccessory.bind(this))

        this.subscribeToMovement()

        this.$emit('noFocus')
    }

    public subscribeToMovement() {
        this.subscribeTo(
            this.$movementDataStore.fetchOne(this.$route.params.id),
            movement => {
                this.movement = movement
            },
            'movement',
        )
    }

    public subscribeToMotive() {
        const motiveId = movementSelectors.motive(this.movement)

        if (!motiveId) {
            return
        }

        this.subscribeTo(
            this.$thesaurusTermDataStore.fetchOne(motiveId),
            thesaurusTerm => {
                this.motive = getTranslatedString(
                    thesaurusTermSelectors.preferredTerm(thesaurusTerm),
                    this.getAppLocale()
                )
            },
            'motive',
        )
    }

    public subscribeToAssociatedTask() {
        const associatedTaskId = movementSelectors.associatedTasks(this.movement)[0]

        if (!associatedTaskId) {
            return
        }

        this.subscribeTo(
            this.$taskDataStore.fetchOne(associatedTaskId),
            associatedTask => {
                this.associatedTask = associatedTask
            },
            'associatedTask',
        )
    }

    public subscribeToItems() {
        const linkableItems = movementSelectors.linkableItems(this.movement)
        const state = movementSelectors.state(this.movement)

        linkableItems.filter(linkableItem => {
            switch (state) {
                default:
                    return true

                case MovementLifecycleStates.FINALIZED:
                case MovementLifecycleStates.ARCHIVED:
                case MovementLifecycleStates.CANCELED:
                    return (
                        linkableItem.state &&
                        linkableItem.state.name === LinkableItemLifecycleStates.FINALIZED
                    )
            }
        })

        if (!linkableItems || linkableItems.length === 0) {
            this.items = []
            this.containers = []
            this.accessories = []
            return
        }

        const itemIds = linkableItems.filter(
                linkableItemResource => linkableItemResource.linkableItem?.linkableItem
            ).map(
                linkableItemResource => linkableItemResource.linkableItem!.linkableItem as string
            )

        this.subscribeTo(
            combineLatest(
                _.uniq(itemIds).map(item =>
                    this.$itemDataStore.fetchIfHasAccess(item),
                ),
            ).pipe(
                map(items => items.filter(_.identity) as Item[]),
                tap(
                    items =>
                        (this.containers = items
                            .filter(item => item.type.includes('Container'))
                            .map(item => {
                                return {
                                    item: item,
                                    wrapperType: 'Item',
                                    itemType: 'Container',
                                } as ItemTypeWrapper
                            })
                        ),
                ),
                map(items => items.filter(item => !item.type.includes('Container'))),
                tap(
                    items =>
                        (this.accessories = items.filter(item => item.type.includes('Accessory'))),
                ),
                map(items => items.filter(item => !item.type.includes('Accessory'))),
                tap(() => (this.currentlyAddingTo = null)),
            ),
            items => {
                this.items = items.sort(itemSorters.byRealTimeLocation) as Item[]
            },
            'items',
        )
    }

    @Watch('$route', { deep: true })
    public onRouteChange() {
        // to hide the reset of the puller, we reset the state of this page to make it look like the same as the mockup view
        this.items = [null, null, null] as any[]
        this.$refs.puller.reset()

        this.refreshMovement()

        this.focusedItemId = null as any
    }

    @Watch('movement')
    public onEntityChange() {
        this.unsubscribeFrom('motive')
        this.unsubscribeFrom('associatedTask')
        this.unsubscribeFrom('items')
        this.subscribeToAssociatedTask()
        this.subscribeToMotive()
        this.subscribeToItems()
    }

    public onPulled() {
        // the user has reach pull-right non return point, we must transition to the next entity
        const next = this.$route.query.next as any
        const id = (next as string[]).shift() as string

        requestAnimationFrame(() =>
            router.push({
                name: this.$route.name || '',
                params: { id },
                query: (next.length > 0 ? { next } : {}) as Record<string, string>,
            }),
        )
    }

    public focusItem(item: Item) {
        const id = itemSelectors.id(item)

        if (this.committedItems.indexOf(id) !== -1) {
            return // we can't focus already validated items
        }

        this.$emit('focusedItem')

        this.focusedItemId = id
    }

    public rollbackItem(item: Item) {
        if (!this.isItemCommitted(item)) {
            return
        }

        const id = itemSelectors.id(item)
        const index = this.committedItems.indexOf(id)
        this.committedItems.splice(index, 1)
    }

    public commitItem(item: Item) {
        const id = itemSelectors.id(item)

        if (this.focusedItemId === id) {
            this.unfocusItem() // we can't focus already validated items
        }

        if (this.isItemCommitted(item)) {
            return
        }

        this.committedItems.push(id)
    }

    private unfocusItem() {
        this.focusedItemId = null as any
        this.$emit('noFocus')
    }

    public hasItemFocus(item: Item) {
        return this.focusedItemId === itemSelectors.id(item)
    }

    public isItemCommitted(item: Item) {
        return this.committedItems.indexOf(itemSelectors.id(item)) !== -1
    }

    public toggleCollapse() {
        this.showCollapse = !this.showCollapse
    }

    public goToAssociatedTask(event: MouseEvent) {
        event.preventDefault()
        event.stopPropagation()

        if (!this.associatedTask) {
            return
        }
        this.$router.push(`/tasks/${this.associatedTask.id}`)
    }

    /**
     * Add item to movement's linkableItems and refresh movement data
     * @param item
     */
    private async onAddedItemSelected(item: { id: string; type: string }) {
        try {
            this.currentlyAddingTo = getContentTypeFromTypeKey(item.type)
            await this.$movementDataStore.addItem(
                this.movement.id,
                item.id,
                getClassNameFromContentTypeKey(item.type),
            )
            this.unsubscribeFrom('movement')
            this.subscribeToMovement()
        } catch (error) {
            console.error(error)
            this.currentlyAddingTo = null
        } finally {
            this.$refs.addItemModal?.resetSearch()
            this.$refs.addAccessoryModal?.resetSearch()
            this.$refs.addContainerModal?.resetSearch()
        }
    }

    private get linkableItems() {
        if (this.movement) {
            return movementSelectors.linkableItems(this.movement).map(item => item.linkableItem)
        }
    }

    private askConfirmationForReturnMovement() {
        this.$refs.returnMovementModal.show()
    }

    private async createReturnMovement() {
        this.movementCreationPending = true

        let movement: Movement
        try {
            movement = await (this.$movementDataStore as MovementDataStore).createReturnMovement(
                this.movement.id,
            )
        } finally {
            this.movementCreationPending = false
        }

        if (movement) {
            router.push({
                name: this.$route.name || '',
                params: { id: movement.id },
            })
        }
    }

    private addItem() {
        this.unfocusItem() // to prevent from filling the input if scan mode is enable
        this.$refs.addItemModal.open()
    }

    private addContainer() {
        this.$refs.addContainerModal.open()
    }

    private addAccessory() {
        this.unfocusItem() // to prevent from filling the input if scan mode
        this.$refs.addAccessoryModal.open()
    }

    private closeMovement() {
        this.unfocusItem() // to prevent from filling the input if scan mode is enable
        if (this.$refs.closeMovementModal) {
            this.$refs.closeMovementModal.open()
        }
    }

    private onMovementClosed() {
        this.emitOnBus(STOP_ASKING_FOR_MISSING_LOCATION_TOPIC)
        this.refreshMovement()
    }

    private cancelMovement() {
        this.waitForUserApproval(this.$t('entity.cancelMovement') as string)

        return (this.$movementDataStore as MovementDataStore).cancelMovement(this.movement.id)
    }

    private deleteMovement() {
        if(this.movement.id) {
            this.$movementDataStore.delete(this.movement.id).subscribe(() => {
                this.$router.push({ name: Routes.MOVEMENTS })
            })
        }
    }

    private refreshMovement() {
        this.unsubscribeFrom('movement')
        this.subscribeToMovement()
    }

    private async startItemTransit() {
        if (this.linkableItems) {
            await this.$movementDataStore.transitionLinkableItems(
                this.movement,
                LinkableItemLifecycleStates.IN_TRANSIT,
                undefined, // undefined for transitionning all items ([] is not considered undefined)
            )
            this.refreshMovement()
        }
    }

    private async onItemLocationChanged(event) {
        if (event.itemId && event.location) {
            if (this.movement) {
                await this.$movementDataStore.transitionLinkableItem(
                    movementSelectors.id(this.movement),
                    event.itemId,
                    LinkableItemLifecycleStates.FINALIZED,
                    locationSelectors.id(event.location),
                )
                this.refreshMovement()
            }
        }
    }

    private onItemUpdated(event, itemType: ItemSorts) {
        // @ts-ignore
        let dataStore: DataStore
        let list: Item[] | ItemTypeWrapper[]

        switch(itemType) {
            case 'CONTAINER':
                dataStore = this.$containerDataStore
                list = this.containers
                break
            case 'ACCESSORY':
                dataStore = this.$itemDataStore
                list = this.accessories
                break
            default:
                dataStore = this.$itemDataStore
                list = this.items
                break
        }

        if(event.itemId && event.changes) {

            if(itemType === 'CONTAINER') {
                const typedList = list as ItemTypeWrapper[]
                const itemToUpdateIndex = typedList.findIndex(item => (item.item as Container).id === event.itemId)
                if(itemToUpdateIndex >= 0) {
                    const body = dataStore.mergeOverridingArrays(
                        typedList[itemToUpdateIndex].item, 
                        event.changes
                    )

                    dataStore.update(
                        (typedList[itemToUpdateIndex].item as Container).id, 
                        body
                    )
                    .subscribe(itemUpdated => {
                        // works because list is a reference ("pointer") on this.containers, ...
                        list.splice(
                            itemToUpdateIndex, 
                            1, 
                            {
                                item: itemUpdated,
                                wrapperType: 'Item',
                                itemType: 'Container',
                            }
                        )
                    })
                }

            } else {
                const typedList = list as Item[]
                const itemToUpdateIndex = typedList.findIndex(item => item.id === event.itemId)
                if(itemToUpdateIndex >= 0) {
                    const body = dataStore.mergeOverridingArrays(typedList[itemToUpdateIndex], event.changes)

                    dataStore.update(
                        typedList[itemToUpdateIndex].id, 
                        body
                    )
                    .subscribe((itemUpdated) => {
                        list.splice(itemToUpdateIndex, 1, itemUpdated) // works because list is a reference ("pointer") on this.items or this.containers, ...
                    })
                }
            }

        }
    }

    private toggleCloseContainer(container: Container) {
        this.containers.splice(
            this.containers.findIndex(listItem => container.id === (listItem.item as Container).id),
            1,
            {
                item: container,
                itemType: 'Container',
                wrapperType: 'Item',
            } as ItemTypeWrapper,
        )
    }

    private get state() {
        return movementSelectors.state(this.movement)
    }

    public async removeItem(itemId: string) {
        this.$movementDataStore.removeItem(this.movement.id, itemId).subscribe(this.refreshMovement)
    }
}
